import { useCuratorMatchingTags } from '~/composables/useCuratorMatchingTags'

import { useTagStore } from '~/stores/tag'

import type {
  Influencer,
  InfluencerLikedNeutralHatedTags,
  InfluencerPreferences,
} from '~/types/influencer'
import type TagInformation from '~/types/tagInformations'

export function useCuratorProfileAbout(
  influencerRef: MaybeRefOrGetter<Influencer>,
) {
  const influencer = toValue(influencerRef)
  const { orderedTargets } = useCuratorMatchingTags(() => influencer)

  const { GET_TAGS_FROM_IDS } = useTagStore()

  const genresConfiguration = computed<InfluencerPreferences>(() => {
    if (!influencer.tags) {
      return {
        liked: [],
        neutral: [],
        hated: [],
      } satisfies InfluencerPreferences
    }

    return orderedTargets.reduce<InfluencerPreferences>((acc, target) => {
      const typeNames: (keyof InfluencerLikedNeutralHatedTags)[] = [
        'subgenre',
        'mood',
        'format',
        'artist_kind',
      ]
      const additionalTagInformations = typeNames.reduce<TagInformation[]>(
        (accumulator, typeName) => {
          const partialTagInformations = GET_TAGS_FROM_IDS([
            ...new Set((influencer.tags[target] ?? {})[typeName] ?? []),
          ]).map((tag) => {
            return {
              typeName,
              tag,
            } satisfies TagInformation
          })
          if (!hasContent(partialTagInformations)) return accumulator

          return [...accumulator, ...partialTagInformations]
        },
        [],
      )
      if (!hasContent(additionalTagInformations)) return acc

      acc[target] = additionalTagInformations
      return acc
    }, {} as InfluencerPreferences)
  })

  const showVerifiedBadge = computed<boolean>(() => influencer.authorized)

  const hasBadgeTags = computed<boolean>(
    () =>
      (influencer.groover_note?.length ?? 0) > 0 ||
      (influencer.tags?.identity.influencer_badge?.length ?? 0) > 0 ||
      Number(influencer.cost) > 2,
  )

  function hasContent(tagInformations: TagInformation[]): boolean {
    return Boolean(tagInformations.length)
  }

  return {
    genresConfiguration,
    hasBadgeTags,
    showVerifiedBadge,
  }
}
